import React, {useEffect, useReducer, useRef} from "react";
import Button from "../../common/Button";
import {Breadcrumb, Steps} from "antd";
import StickyBlock, {POSITION} from "../../common/StickyBlock";
import useCurrentShortcut from "../../hooks/useCurrentShortcut";
import UploadClassList, {UploadClassListRef} from "./UploadClassList";
import ConfigureClasses, {ConfigureClassesRef} from "./ConfigureClasses";
import ConfigureUsers, {ConfigureUsersRef} from "./ConfigureUsers";
import ReviewAndSubmit, {ReviewAndSubmitRef} from "./ReviewAndSubmit";
import {VerifiedIcon} from "../../common/icons";
import CircularLoader from "../../common/CircularLoader";
import {useBreadcrumbComponent} from "../../common/useBreadcrumbComponent";
import {useLocation} from "wouter";

export interface User {
    userId: number, firstName: string, lastName: string, username: string, matchedUser: boolean, currentClasses: number[], matchedClasses: string[], classes: string[], error: typeof ERRORS[keyof typeof ERRORS], subscriberType: string, removed: REMOVE_TYPES, sequence: number
}
export interface Clazz {
    id: number, className: string, matchedClass: boolean, suggestedMatchId: string | number, matchId: string | number
}
export const STEPS: Record<string, {title: string, sequence: number}> = {
    UPLOAD: {title: 'Upload Class List', sequence: 0},
    CLASS_MATCH: {title: 'Class Match', sequence: 1},
    MANAGE_USERS: {title: 'Manage Users', sequence: 2},
    REVIEW_SUBMIT: {title: 'Review and Submit', sequence: 3}
}
export const ERRORS: Record<string, {name: string, tooltip: string, description: string}> = {
    NULL: {name: "null", tooltip: "", description: ""},
    REMOVING: {name: "removing", tooltip: "", description: ""},
    USER_NOT_FOUND: {name: "userNotFound", tooltip: "User not found. Type a new username to search.", description: "user not found"},
    NO_CLASSES: {name: "noClasses", tooltip: "User has no classes assigned. Assign class in New Classes field.", description: "no classes"}
}
export enum REMOVE_TYPES {
    NULL = 'null',
    REMOVING = 'removing',
    REMOVED = 'removed'
}
export enum COMPLETED_TYPES {
    ACTIVE = 'active',
    LOADING = 'loading',
    COMPLETED = 'completed'
}
enum ActionTypes {
    HYDRATE_PROCESSED_DATA = 'hydrateProcessedData',
    PREPARE_DATA = 'prepareData',
    UPDATE_CLASS_MATCH = 'updateClassMatch',
    UPDATE_USER = 'updateUser',
    UPDATE_USER_CLASSES = 'updateUserData',
    CLEAR_USER_ERRORS = 'clearUserErrors',
    REMOVE_USER = 'removeUser',
    REVIEW_DATA = 'reviewData',
    UPDATE_CLASSES = 'updateClasses',
    UPDATE_STEP = 'updateStep',
    COMPLETE_UPDATE = 'completeUpdate'
}
type Action =
    | { type: ActionTypes.HYDRATE_PROCESSED_DATA; payload: {users: Record<number, User>, classes: Record<string, Clazz>, schoolClasses: Record<number, object>, validUserCount: number, invalidUserCount: number }}
    | { type: ActionTypes.PREPARE_DATA;}
    | { type: ActionTypes.UPDATE_CLASS_MATCH; payload: {classKey: string, matchId: string | number}}
    | { type: ActionTypes.UPDATE_USER; payload: {userId: number, userDetails: object}}
    | { type: ActionTypes.UPDATE_USER_CLASSES; payload: {userId: number, classes: string[]}}
    | { type: ActionTypes.CLEAR_USER_ERRORS; payload: {userId: number}}
    | { type: ActionTypes.REMOVE_USER; payload: {userId: number, removeType: REMOVE_TYPES}}
    | { type: ActionTypes.REVIEW_DATA;}
    | { type: ActionTypes.UPDATE_CLASSES; payload: {loading: boolean}}
    | { type: ActionTypes.COMPLETE_UPDATE;}
    | { type: ActionTypes.UPDATE_STEP; payload: {step: number}};

interface State {
    users: Record<number, User>,
    classes: Record<string, Clazz>,
    newClasses: Record<string, Clazz>,
    schoolClasses: Record<number, object>,
    step: number,
    completed: COMPLETED_TYPES,
    validUserCount: number,
    invalidUserCount: number
}
const defaultState :State = {
    users: {},
    classes: {},
    newClasses: {},
    schoolClasses: {},
    step: 0,
    validUserCount: 0,
    invalidUserCount: 0,
    completed: COMPLETED_TYPES.ACTIVE
}

const reducer: React.Reducer<State, Action> = (state, action) => {
    if (action.type === ActionTypes.HYDRATE_PROCESSED_DATA) {
        return {
            ...state,
            users: Object.fromEntries(
                Object.entries(action.payload.users).map(([key, user], index) => {
                    return [key, {
                        ...user,
                        error: ERRORS.NULL,
                        removed: REMOVE_TYPES.NULL,
                        sequence: index
                    }]
                })
            ),
            classes: Object.fromEntries(
                Object.entries(action.payload.classes).map(([key, clazz]) => {
                    let mapping: string | number = key;
                    if (clazz.matchId) mapping = clazz.matchId;
                    else if (clazz.id) mapping = clazz.id;
                    else if (clazz.suggestedMatchId) mapping = clazz.suggestedMatchId;
                    return [
                        key,
                        {
                            ...clazz,
                            matchId: mapping
                        }
                    ];
                })
            ),
            schoolClasses: action.payload.schoolClasses,
            validUserCount: action.payload.validUserCount,
            invalidUserCount: action.payload.invalidUserCount
        }
    } else if (action.type === ActionTypes.PREPARE_DATA) {
        const updatedUsers: Record<number, User> = prepareUsersData(state.users, state.classes, state.schoolClasses);
        let newClasses: Record<string, Clazz> = {};
        for (let [key, clazz] of Object.entries(state.classes)) {
            if (typeof clazz.matchId !== 'number') {
                newClasses[key] = state.classes[clazz.matchId];
            }
        }
        return {
            ...state,
            users: updatedUsers,
            newClasses: newClasses,
            step: STEPS.MANAGE_USERS.sequence
        }
    } else if (action.type === ActionTypes.UPDATE_CLASS_MATCH) {
        return {
            ...state,
            classes: {
                ...state.classes,
                [action.payload.classKey]: {
                    ...state.classes[action.payload.classKey],
                    matchId: action.payload.matchId
                }
            }
        }
    } else if (action.type === ActionTypes.UPDATE_USER) {
        const existingUser = state.users[action.payload.userDetails.userId];
        console.log('action.payload', action.payload);
        console.log('existingUser', existingUser);
        let updatedUsers: Record<number, User> = {};
        Object.entries(state.users).map(([key, user]) => {
            console.log('user', user);
            if (user.userId == action.payload.userId) {
                const updatedUser: User = {...user, ...action.payload.userDetails};
                if (existingUser != null) {
                    updatedUser.matchedClasses = [...new Set([...existingUser.matchedClasses, ...updatedUser.matchedClasses])];
                }
                if (action.payload.userDetails.matchedUser) {
                    updatedUsers[action.payload.userDetails.userId] = updatedUser;
                } else {
                    let newId: number = user.userId;
                    if (user.matchedUser) {
                        const filteredValues: User[] = Object.values(state.users)
                            .filter(user => user.userId < 1)
                            .sort((a, b) => b.userId - a.userId);
                        newId = filteredValues.length > 0 ? filteredValues[filteredValues.length - 1].userId - 1 : -1;
                    }
                    updatedUsers[newId] = {...updatedUser, userId: newId};
                }
            } else if (existingUser == null || user.userId != action.payload.userDetails.userId) {
                updatedUsers[key] = user;
            }
        });
        const preparedUpdatedUsers: Record<number, User> = prepareUsersData(updatedUsers, state.classes, state.schoolClasses);

        return {
            ...state,
            users: preparedUpdatedUsers,
            validUserCount: Object.values(preparedUpdatedUsers).filter(user => user.removed === 'null' && user.userId > 0).length,
            invalidUserCount: Object.values(preparedUpdatedUsers).filter(user => user.removed === 'null' && user.userId <= 0).length
        }
    } else if (action.type === ActionTypes.UPDATE_USER_CLASSES) {
        const users = prepareUsersData({
            ...state.users,
            [action.payload.userId]: {
                ...state.users[action.payload.userId],
                classes: action.payload.classes
            }
        }, state.classes, state.schoolClasses, false);

        return {
            ...state,
            users: users,
            validUserCount: Object.values(users).filter(user => user.removed === 'null' && user.userId > 0).length,
            invalidUserCount: Object.values(users).filter(user => user.removed === 'null' && user.userId <= 0).length
        }
    } else if (action.type === ActionTypes.CLEAR_USER_ERRORS) {
        const users =  {
            ...state.users,
            [action.payload.userId]: {
                ...state.users[action.payload.userId],
                error: ERRORS.NULL
            }
        };
        return {
            ...state,
            users: users,
            validUserCount: Object.values(users).filter(user => user.removed === 'null' && user.userId > 0).length,
            invalidUserCount: Object.values(users).filter(user => user.removed === 'null' && user.userId <= 0).length
        }
    } else if (action.type === ActionTypes.REMOVE_USER) {
        console.log('Remove user', action.payload);
        const users = {
            ...state.users,
            [action.payload.userId]: {
                ...state.users[action.payload.userId],
                removed: action.payload.removeType
            }
        };

        return {
            ...state,
            users: users,
            validUserCount: Object.values(users).filter(user => user.removed === 'null' && user.userId > 0).length,
            invalidUserCount: Object.values(users).filter(user => user.removed === 'null' && user.userId <= 0).length
        }
    } else if (action.type === ActionTypes.REVIEW_DATA) {
        return {
            ...state,
            step: STEPS.REVIEW_SUBMIT.sequence
        }
    } else if (action.type === ActionTypes.UPDATE_CLASSES) {
        return {
            ...state,
            completed: action.payload.loading ? COMPLETED_TYPES.LOADING : COMPLETED_TYPES.ACTIVE
        }
    } else if (action.type === ActionTypes.COMPLETE_UPDATE) {
        return {
            ...state,
            completed: COMPLETED_TYPES.COMPLETED
        }
    } else if (action.type === ActionTypes.UPDATE_STEP) {
        return {...state, step: action.payload.step}
    } else {
        return state;
    }
}

const prepareUsersData = (users: Record<number, User>, classes: Record<string, Clazz>, schoolClasses: Record<number, object>, updateClasses: boolean = true): Record<number, User> => {
    return Object.fromEntries(
        Object.entries(users).map(([key, user]) => {
            let preparedUser: User = {...user};
            if (updateClasses) {
                const userMappedClasses: any[] = [];
                user.matchedClasses.forEach(className => {
                    if (className && classes[className]) {
                        if (typeof classes[className].matchId === 'number') {
                            userMappedClasses.push(schoolClasses[classes[className].matchId].className);
                        } else {
                            userMappedClasses.push(classes[className].matchId);
                        }
                    }
                });
                preparedUser.classes = userMappedClasses;
            }
            const error = preparedUser.matchedUser ? (preparedUser.classes.length > 0 ? ERRORS.NULL : ERRORS.NO_CLASSES) : ERRORS.USER_NOT_FOUND;
            const hadErrors = user.error !== ERRORS.NULL && user.error !== ERRORS.REMOVING;
            return [key, {
                ...preparedUser,
                error: (hadErrors && error === ERRORS.NULL ? ERRORS.REMOVING : error)
            }];
        })
    );
}

const BulkClassUpdate: React.FC = props => {
    const {exitMode} = props;
    const [state, dispatch] = useReducer(reducer, defaultState);
    const {loadShortcutByUrl} = useCurrentShortcut();
    const uploadClassListRef = useRef<UploadClassListRef>();
    const configureClassesRef = useRef<ConfigureClassesRef>();
    const configureUsersRef = useRef<ConfigureUsersRef>();
    const reviewAndSubmitRef = useRef<ReviewAndSubmitRef>();
    const [location] = useLocation<String>();

    useEffect(() => {
        if (state != null && state.users != null) {
            Object.values(state.users).forEach(user => {
                if (user.error === ERRORS.REMOVING) {
                    setTimeout(() => {
                        dispatch({ type: ActionTypes.CLEAR_USER_ERRORS, payload: { userId: user.userId } });
                    }, 1500);
                }
            });
        }
    }, [state.users]);

    const handleExitMode = (): void => {
        exitMode();
    }
    const hydrateProcessedData = ({users, classes, schoolClasses, validUserCount, invalidUserCount}: {users: Record<number, User>, classes: Record<string, Clazz>, schoolClasses: Record<number, object>, validUserCount: number, invalidUserCount: number}): void => {
        dispatch({type: ActionTypes.HYDRATE_PROCESSED_DATA, payload: {users: users, classes: classes, schoolClasses: schoolClasses, validUserCount, invalidUserCount: invalidUserCount}});
    }
    const startImport = (): void => {
        dispatch({type: ActionTypes.UPDATE_STEP, payload: {step: STEPS.CLASS_MATCH.sequence}});
    }
    const updateClassMatch = (classKey: string, matchId: number | string): void => {
        dispatch({type: ActionTypes.UPDATE_CLASS_MATCH, payload: {classKey: classKey, matchId: matchId}})
    }
    const prepareData = (): void => {
        dispatch({type: ActionTypes.PREPARE_DATA});
    }
    const updateUser = (userId: number, userDetails: object): void => {
        dispatch({type: ActionTypes.UPDATE_USER, payload: {userId: userId, userDetails: userDetails}});
    }
    const updateUserClasses = (userId: number, classes: string[]): void => {
        dispatch({type: ActionTypes.UPDATE_USER_CLASSES, payload: {userId: userId, classes: classes}});
    }
    const removeUser = (userId: number): void => {
        dispatch({type: ActionTypes.REMOVE_USER, payload: {userId: userId, removeType: REMOVE_TYPES.REMOVED}});
    }
    const reviewData = (): void => {
        dispatch({type: ActionTypes.REVIEW_DATA});
    }
    const updateClasses = (loading: boolean): void => {
        dispatch({type: ActionTypes.UPDATE_CLASSES, payload: {loading: loading}});
    }
    const completeUpdate = (): void => {
        dispatch({type: ActionTypes.COMPLETE_UPDATE});
    }
    const prevStep = (): void => {
        dispatch({type: ActionTypes.UPDATE_STEP, payload: {step: Math.max(state.step - 1, 0)}});
    }

    const BreadcrumbBar = useBreadcrumbComponent({ dropLast: false, dropEndingSeparator: true });

    return (
        <div className="flexColumn flexAlignCentre" style={{minHeight: '100%'}}>
            <StickyBlock className='padding-b--l' scrollElement={document.getElementById('contentPanel')} style={{width: '100%'}}>
                {location === '/web/ui/view/classes/schoolClasses/bulkClassUpdate'
                    ? <BreadcrumbBar />
                    : <Breadcrumb items={[
                        {
                            title: 'Dashboard',
                            href: '',
                            onClick: (event): void => {
                                event.preventDefault();
                                loadShortcutByUrl('/dashboard/loadActivityDashboard.action');
                            }
                        },
                        {
                            title: 'Manage classes',
                            href: '',
                            onClick: (event): void => {
                                event.preventDefault();
                                handleExitMode();
                            }
                        },
                        {
                            title: 'Class Import'
                        }
                    ]}/>}
                {state.completed == COMPLETED_TYPES.ACTIVE && (
                    <Steps
                        className="margin-t--m"
                        size="small"
                        current={state.step}
                        items={[
                            {title: STEPS.UPLOAD.title},
                            {title: STEPS.CLASS_MATCH.title},
                            {title: STEPS.MANAGE_USERS.title},
                            {title: STEPS.REVIEW_SUBMIT.title}
                        ]}
                    />
                )}
            </StickyBlock>
            {state.completed == COMPLETED_TYPES.ACTIVE && (
                <>
                    <div className="margin-t--l" style={{maxWidth: '1000px', width: '100%', flexGrow: 1}}>
                        {state.step === STEPS.UPLOAD.sequence && (
                            <UploadClassList
                                ref={uploadClassListRef}
                                hasData={Object.entries(state.users).length > 0}
                                hydrateProcessedData={hydrateProcessedData} startImport={startImport}
                            />
                        )}
                        {state.step === STEPS.CLASS_MATCH.sequence && (
                            <ConfigureClasses
                                ref={configureClassesRef}
                                classes={state.classes}
                                schoolClasses={state.schoolClasses}
                                updateClassMatch={updateClassMatch}
                                prepareData={prepareData}
                                style={{width: '100%'}}
                            />
                        )}
                        {state.step === STEPS.MANAGE_USERS.sequence && (
                            <ConfigureUsers
                                ref={configureUsersRef}
                                users={state.users}
                                newClasses={state.newClasses}
                                schoolClasses={state.schoolClasses}
                                updateUser={updateUser}
                                updateUserClasses={updateUserClasses}
                                removeUser={removeUser}
                                reviewData={reviewData}
                            />
                        )}
                        {state.step === STEPS.REVIEW_SUBMIT.sequence && (
                            <ReviewAndSubmit
                                ref={reviewAndSubmitRef}
                                users={state.users}
                                newClasses={state.newClasses}
                                schoolClasses={state.schoolClasses}
                                validUserCount={state.validUserCount}
                                invalidUserCount={state.invalidUserCount}
                                updateClasses={updateClasses}
                                completeUpdate={completeUpdate}
                            />
                        )}
                    </div>
                    <StickyBlock
                        className='margin-t--l padding-y--l'
                        position={POSITION.BOTTOM}
                        scrollElement={document.getElementById('contentPanel')}
                        style={{maxWidth: '1000px', width: '100%'}}
                    >
                        <div className="flexSpaceBetween">
                            {state.step > 0 ? (
                                <Button onClick={prevStep}>Back</Button>
                            ) : (
                                <div></div>
                            )}
                            {(state.step != STEPS.REVIEW_SUBMIT.sequence || state.validUserCount > 0) &&
                                <Button
                                    onClick={() => {
                                        if (state.step === STEPS.UPLOAD.sequence) {
                                            if (uploadClassListRef.current) uploadClassListRef.current.checkNextStep();
                                        } else if (state.step === STEPS.CLASS_MATCH.sequence) {
                                            if (configureClassesRef.current) configureClassesRef.current.checkNextStep();
                                        } else if (state.step === STEPS.MANAGE_USERS.sequence) {
                                            if (configureUsersRef.current) configureUsersRef.current.checkNextStep();
                                        } else if (state.step === STEPS.REVIEW_SUBMIT.sequence) {
                                            if (reviewAndSubmitRef.current) reviewAndSubmitRef.current.checkNextStep();
                                        }
                                    }}
                                    disabled={Object.entries(state.users).length === 0}
                                >
                                    {state.step === STEPS.UPLOAD.sequence ? (
                                        <>Begin Import</>
                                    ) : state.step === STEPS.REVIEW_SUBMIT.sequence ? (
                                        <>Submit Import</>
                                    ) : (
                                        <>Next</>
                                    )}
                                </Button>
                            }
                        </div>
                    </StickyBlock>
                </>
            )}
            {state.completed == COMPLETED_TYPES.LOADING && (
                <div className="padding--l">
                    <p>This may take a few moments</p>
                    <CircularLoader />
                </div>
            )}
            {state.completed == COMPLETED_TYPES.COMPLETED && (
                <div className="ch-center-elem flexColumn">
                    <h1 className="margin-t--xl">Import Success</h1>
                    <VerifiedIcon height={112} width={112} className="margin-t--l" style={{fill: 'var(--success-colour)'}}/>
                    <p className="margin-t--l">Your classes have been successfully updated.</p>
                    <Button className="margin-t--l" onClick={handleExitMode}>Manage classes</Button>
                </div>
            )}
        </div>
    )
}

export default BulkClassUpdate;